import { isNative } from '~/plugins/native/capacitor'

// Initialise over the air updates from our self hosted OTA system
export default async function ({ store, app, $log, redirect, route }, inject) {
  const otaUpdates = {}

  if (isNative) {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    const { SplashScreen } = await import('@capacitor/splash-screen')

    let data = null

    $log.debug('OTA notifying ready')

    CapacitorUpdater.notifyAppReady()

    // Remove stale OTA bundles so an install's "Documents & Data" doesn't balloon over
    // time. We keep the running bundle, the built-in bundle, any bundle queued to load on
    // next launch (status "pending"), and the most recent previous successful bundle so a
    // rollback is still possible. Everything else is deleted.
    otaUpdates.cleanupOldBundles = async () => {
      try {
        const current = await CapacitorUpdater.current()
        const currentId = current?.bundle?.id

        if (!currentId) {
          return
        }

        const { bundles } = await CapacitorUpdater.list()

        if (!bundles?.length) {
          return
        }

        const keepIds = new Set([currentId, 'builtin'])

        // Never remove a bundle that is queued to load on the next launch.
        bundles.filter(bundle => bundle.status === 'pending').forEach(bundle => keepIds.add(bundle.id))

        // Keep the most recently downloaded previous successful bundle for rollback.
        const [previousSuccessfulBundle] = bundles
          .filter(bundle => bundle.status === 'success' && bundle.id !== currentId && bundle.id !== 'builtin')
          .sort((a, b) => new Date(b.downloaded) - new Date(a.downloaded))

        if (previousSuccessfulBundle) {
          keepIds.add(previousSuccessfulBundle.id)
        }

        for (const bundle of bundles) {
          if (keepIds.has(bundle.id)) {
            continue
          }

          try {
            await CapacitorUpdater.delete({ id: bundle.id })

            $log.debug('OTA deleted old bundle', bundle.version)
          } catch (error) {
            $log.debug('OTA failed to delete bundle', bundle.id, error)
          }
        }
      } catch (error) {
        $log.debug('OTA cleanup error', error)
      }
    }

    otaUpdates.sync = async isActive => {
      if (process.env.APP_ENVIRONMENT === 'local') {
        return false
      }

      const currentBundle = await CapacitorUpdater.current()

      if (currentBundle.bundle?.version) {
        store.commit('device/setBundleVersion', currentBundle.bundle.version)
      }

      const isOnCheckoutPage = app.router.history?.current?.path?.includes('checkout')
      const isOnAuthPage = app.router.history?.current?.path?.includes('auth/')

      if (isActive) {
        store.commit('overTheAirUpdates/setStatusCode', 5)

        try {
          const latest = await CapacitorUpdater.getLatest()

          // $log.debug('OTA latest', latest)

          if (latest.url) {
            store.commit('overTheAirUpdates/setStatusCode', 7)

            $log.debug('OTA downloading...')

            data = await CapacitorUpdater.download({
              url: latest.url,
              version: latest.version
            })

            $log.debug('OTA finished downloading')

            store.commit('overTheAirUpdates/setStatusCode', 6)
          } else {
            store.commit('overTheAirUpdates/setStatusCode', 0)
          }
        } catch (error) {
          console.log('OTA threw error', error)

          store.commit('overTheAirUpdates/setStatusCode', 0)
        }
      } else if (!isActive && data && !isOnCheckoutPage && !isOnAuthPage) {
        $log.debug('OTA: Setting new version')
        // Do the switch when user leave app or when you want
        SplashScreen.show()

        try {
          store.commit('overTheAirUpdates/setStatusCode', 8)

          await CapacitorUpdater.set({ id: data.id })

          store.commit('overTheAirUpdates/setStatusCode', 1)

          redirect('/platform')
        } catch (error) {
          $log.debug('OTA error', error)

          store.commit('overTheAirUpdates/setStatusCode', 3)

          store.commit('overTheAirUpdates/setError', error)

          SplashScreen.hide()
        }
      }
    }

    otaUpdates.setUserId = async userId => {
      if (!userId) {
        return false
      }

      await CapacitorUpdater.setCustomId({ customId: userId })
    }

    // Run sync first, then prune bundles once it settles so cleanup never races a
    // download in progress. Catching sync errors avoids unhandled-rejection warnings.
    otaUpdates
      .sync(true)
      .catch(error => $log.debug('OTA sync error', error))
      .then(() => otaUpdates.cleanupOldBundles())

    const { App } = await import('@capacitor/app')

    App.addListener('appStateChange', appState => {
      otaUpdates.sync(appState.isActive)
    })
  }

  inject('otaUpdates', otaUpdates)
}
